import { TokenResponse, } from './models/Auth';
import { persistanceStorage, store } from '../redux/Store';
import networkConfig from '../network/config';
import App from '../App';
import { getTokens, clearTokens, renewAccessToken, userSessionExpired, logoutAction, signInFailed, } from '../redux/Actions/AuthAction';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import { updateCustomerID } from '../redux/Actions/CartAction';
import { deleteCustomerAccount, getCustomerInfo, resetCustomerInfo } from '../redux/Actions/OrderservAction';
import DayJsUtil from '../utils/DayJSUtils';
import SharedUtils from '../utils/SharedUtils';
import { setUserParams } from '../redux/Actions/AppStateAction';
import { encrypt, decrypt, config } from '../config/config.utils';
import url from 'url';
import { ENCRYPTION_DECRYPTION_ERROR, GTM_KEY } from '../config/config.constants';
import { updateCustomerIDforOrder } from '../redux/Actions/orderAction';
class AuthManager {
    constructor() {
        this.MEMBER_USER = 'MEMBER_USER';
        this.GUEST_USER = 'GUEST_USER';
        this.sendAppBaseUrl = (platform, allowXLanguage, signInParams) => platform && allowXLanguage ? `${signInParams}&domain=${App.envVars?.appApiBaseUrl}` : signInParams;
        // To prevent creation of instance from outside the class
    }
    static getInstance() {
        if (!AuthManager.instance) {
            const authManager = new AuthManager();
            authManager.envVars = App.envVars ?? {};
            authManager.authParams = authManager?.envVars?.authParams ?? {};
            AuthManager.instance = authManager;
        }
        return AuthManager.instance;
    }
    get authTokens() {
        return this.tokens;
    }
    get accessToken() {
        this.setTokens();
        return this.isValidAccessToken ? this.tokens?.accessToken?.token : undefined;
    }
    get refreshToken() {
        this.setTokens();
        return this.isValidRefreshToken ? decrypt(this.tokens?.refreshToken?.token) : undefined;
    }
    get customerId() {
        this.setTokens();
        const { customerId } = this.authTokens?.accessToken?.claims ?? {};
        return customerId ?? '';
    }
    get loginMethod() {
        this.setTokens();
        const { loginMethod } = this.authTokens?.accessToken?.claims ?? {};
        return loginMethod ?? '';
    }
    get phoneNumber() {
        this.setTokens();
        const { countryCode } = this.authTokens?.accessToken?.claims ?? {};
        const { phoneNumber } = this.authTokens?.accessToken?.claims ?? {};
        if (countryCode && phoneNumber) {
            const nonLeadingZero = +phoneNumber;
            return countryCode + nonLeadingZero;
        }
        return phoneNumber ?? '';
    }
    get countryCode() {
        this.setTokens();
        const { countryCode } = this.authTokens?.accessToken?.claims ?? {};
        return countryCode ?? '';
    }
    get email() {
        this.setTokens();
        const { email } = this.authTokens?.accessToken?.claims ?? {};
        return email ?? '';
    }
    get firstName() {
        this.setTokens();
        const { givenName } = this.authTokens?.accessToken?.claims ?? {};
        return givenName ?? '';
    }
    get familyName() {
        this.setTokens();
        const { familyName } = this.authTokens?.accessToken?.claims ?? {};
        return familyName ?? '';
    }
    get middleName() {
        this.setTokens();
        const { middleName } = this.authTokens?.accessToken?.claims ?? {};
        return middleName ?? '';
    }
    get username() {
        this.setTokens();
        const { preferredUsername } = this.authTokens?.accessToken?.claims ?? {};
        return preferredUsername ?? '';
    }
    get emailSubscription() {
        this.setTokens();
        const { emailSubscription } = this.authTokens?.accessToken?.claims ?? {};
        return emailSubscription ?? true;
    }
    get phoneSubscription() {
        this.setTokens();
        const { phoneSubscription } = this.authTokens?.accessToken?.claims ?? {};
        return phoneSubscription ?? true;
    }
    get postal_code() {
        this.setTokens();
        const { postal_code } = this.authTokens?.accessToken?.claims ?? {};
        return postal_code ?? '';
    }
    async checkAndGetAuthToken() {
        let timerId;
        let intervalId;
        const accessTokenIntervalMillis = this.authParams?.checkAccessTokenIntervalMillis ?? 100;
        return new Promise((resolve, reject) => {
            intervalId = window.setInterval(() => {
                const authToken = this.accessToken;
                if (authToken) {
                    clearInterval(intervalId);
                    clearTimeout(timerId);
                    resolve(authToken);
                }
            }, accessTokenIntervalMillis);
            timerId = window.setTimeout(() => {
                clearInterval(intervalId);
                const authToken = this.accessToken;
                if (!authToken) {
                    reject('Authtoken is not available');
                }
            }, this.envVars?.requestTimeoutSecs);
        });
    }
    setTokens(response) {
        try {
            if (response) {
                this.tokens = TokenResponse.fromFlatResponse(response);
            }
            else if (isEmpty(this.tokens)) {
                const { accessToken: access_token, refreshToken: refresh_token, expiresIn: expires_in, refreshExpiresIn: refresh_expires_in, } = store?.getState()?.authReducer?.response ?? {};
                if (!isEmpty(access_token) && !isEmpty(refresh_token)) {
                    const authResponse = {
                        access_token,
                        refresh_token,
                        expires_in,
                        refresh_expires_in,
                    };
                    this.tokens = TokenResponse.fromFlatResponse(authResponse);
                }
            }
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error('failed to set tokens, reason: ', e.message);
            if (e.message === ENCRYPTION_DECRYPTION_ERROR) {
                store.dispatch(clearTokens());
                this.resetTokens();
                this.bootstrapAuthentication();
            }
        }
    }
    resetTokens() {
        this.tokens = {};
    }
    resetAccessToken() {
        this.tokens = {
            ...this.tokens,
            accessToken: {},
        };
    }
    get isAccessTokenInvalidOrExpired() {
        this.setTokens();
        return (this.tokens?.accessToken?.claims?.exp ?? 0) - DayJsUtil.nowInEpoch() < 0;
    }
    get isValidAccessToken() {
        return !this.isAccessTokenInvalidOrExpired;
    }
    get isRefreshTokenInvalidOrExpired() {
        this.setTokens();
        return (this.tokens?.refreshToken?.claims?.exp ?? 0) - DayJsUtil.nowInEpoch() < 0;
    }
    get isValidRefreshToken() {
        return !this.isRefreshTokenInvalidOrExpired;
    }
    isMemberRefreshTokenExpiring() {
        this.setTokens();
        return (this.isMember &&
            (this.tokens?.refreshToken?.claims?.exp ?? 0) - DayJsUtil.nowInEpoch() <
                (this.authParams?.refreshTokenExpiringInSecs ?? 0));
    }
    get intervalId() {
        return this.tokenIntervalId;
    }
    set intervalId(value) {
        this.tokenIntervalId = value;
    }
    get isMember() {
        this.setTokens();
        const { phoneNumber, email } = this.tokens?.accessToken?.claims ?? {};
        return !isEmpty(phoneNumber) || !isEmpty(email);
    }
    getCustomerDetails() {
        this.setTokens();
        return this.tokens?.accessToken?.claims;
    }
    async processLogin(loginRedirectUri, successCallback) {
        const { query: { code, state, login_result: loginResult } = {} } = url.parse(loginRedirectUri, true);
        if (code) {
            const redirectUri = await persistanceStorage?.getItem('redirectUri');
            const codeVerifier = await persistanceStorage?.getItem('codeVerifier');
            if (state && redirectUri && codeVerifier) {
                AuthManager.getInstance().resetTokens();
                store.dispatch(getTokens({
                    code,
                    state,
                    redirectUri,
                    codeVerifier,
                    showLoader: true,
                    successCallback,
                }));
                return Promise.resolve('login successful');
            }
        }
        return Promise.reject({ message: 'login failed', loginResult });
    }
    isMemberRequest(requestPayload) {
        return !isEmpty(requestPayload?.code);
    }
    handleSignInFailure(requestPayload) {
        const isSignInAsMember = this.isMemberRequest(requestPayload ?? {}) || this.isMember;
        if (isSignInAsMember) {
            store.dispatch(signInFailed());
            store.dispatch(resetCustomerInfo());
            this.clearIntervalIfExists();
            this.resetTokens();
            store.dispatch(clearTokens());
            store.dispatch(getTokens());
        }
    }
    logout() {
        store.dispatch(logoutAction());
    }
    deleteAccount() {
        const { tenantReducer: { tenantId = '' } = {} } = store.getState() ?? {};
        store.dispatch(deleteCustomerAccount({
            tenantId,
        }));
    }
    refreshAuthentication() {
        // Check if member refresh token is about to expire, then dispatch an action //refreshTokenExpiring
        // take the expiration threshold in seconds, variable name in app.js => refreshTokenExpiringInSec
        // if (this.isMemberRefreshTokenExpiring()) {
        //   store.dispatch(refreshTokenExpiring(true));
        // }
        if (this.isAccessTokenInvalidOrExpired) {
            if (this.isRefreshTokenInvalidOrExpired) {
                const { loggedIn = false } = store?.getState()?.appStateReducer?.userStatus ?? {};
                if (this.isMember || loggedIn) {
                    // dispatch action #1 to ui to show alert
                    this.clearIntervalIfExists();
                    store.dispatch(clearTokens());
                    this.resetTokens();
                    store.dispatch(userSessionExpired(true));
                    store.dispatch(resetCustomerInfo());
                }
                else {
                    this.clearIntervalIfExists();
                    this.resetTokens();
                    // dispatch action #2 to get new access token
                    store.dispatch(getTokens());
                }
            }
            else {
                this.clearIntervalIfExists();
                const isMemberLogin = this.isMember;
                this.resetAccessToken();
                // dispatch action #3 to renew access token
                //Name it as renewAccessToken
                store.dispatch(renewAccessToken(isMemberLogin));
            }
        }
        else {
            return true;
        }
    }
    initiateInterval() {
        const timeout = this.authParams?.tokenIntervalTimeoutSecs;
        this.clearIntervalIfExists();
        this.intervalId = window.setInterval(() => {
            this.refreshAuthentication();
        }, timeout);
    }
    getUrlEncodedFormData(formDataObj) {
        return reduce(formDataObj, (result, value, key) => {
            if (value) {
                const keyValue = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
                result += !result ? keyValue : `&${keyValue}`;
            }
            return result;
        }, '');
    }
    getRequestConfig(requestPayload) {
        const { code, redirectUri: redirect_uri, codeVerifier: code_verifier, clientId, grantType, scope, state, refreshToken, token, } = requestPayload ?? {};
        const { memberClientId, guestClientId, memberGrantType, guestGrantType, memberScope, guestScope } = this.authParams ?? {};
        const isRequestForMember = this.isMemberRequest(requestPayload);
        const payload = {
            client_id: clientId ?? (isRequestForMember ? memberClientId : guestClientId),
            grant_type: grantType ?? (isRequestForMember ? memberGrantType : guestGrantType),
            scope: scope ?? (isRequestForMember ? memberScope : guestScope),
            refresh_token: refreshToken,
            code,
            redirect_uri,
            code_verifier,
            state,
            token,
        };
        const formData = this.getUrlEncodedFormData(payload);
        const endpointConfig = isRequestForMember ? networkConfig.authToken : networkConfig.guestToken;
        return {
            formData,
            endpointConfig,
        };
    }
    clearIntervalIfExists() {
        try {
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = undefined;
            }
        }
        catch (e) { }
    }
    modifyTokensResponse(response, clientId) {
        const { access_token, refresh_token, expires_in: expiresIn, refresh_expires_in: refreshExpiresIn } = response;
        const accessToken = encrypt(access_token);
        const refreshToken = encrypt(refresh_token);
        response.access_token = accessToken;
        response.refresh_token = refreshToken;
        this.setTokens(response);
        this.initiateInterval();
        const { memberClientId, guestClientId } = this.authParams ?? {};
        return {
            accessToken,
            refreshToken,
            expiresIn,
            refreshExpiresIn,
            clientId: clientId ?? (this.isMember ? memberClientId : guestClientId),
        };
    }
    updateBasketWithNewCustomer() {
        const { cartReducer: { cartData: { id: basketId = '', customer: { id: cartCustomerId = '' } = {} } = {} } = {}, tenantReducer: { tenantId = '' } = {}, } = store.getState() ?? {};
        const customerId = this.getCustomerDetails()?.customerId;
        this.changeUserStatus();
        if (basketId && customerId !== cartCustomerId) {
            store.dispatch(updateCustomerID({ tenantId, basketId, customerId }));
        }
    }
    updateRecentOrderWithNewCustomer() {
        const { orderReducer: { pastOrderResponse = [] } = {}, postOrderReducer: { orderAccepted = {} } = {}, tenantReducer: { tenantId = '' } = {}, } = store.getState() ?? {};
        const { id: postOrderId = '', customer: { id: postOrderCustomerId = '' } = {} } = orderAccepted;
        const { id: pastOrderId = '', customer: { id: pastOrderCustomerId = '' } = {} } = pastOrderResponse?.[0] || {};
        if ((postOrderId !== '' && postOrderCustomerId !== this.customerId) ||
            (pastOrderId !== '' && pastOrderCustomerId !== this.customerId)) {
            store.dispatch(updateCustomerIDforOrder({
                tenantId,
                customerId: this.customerId,
                orderId: postOrderId || pastOrderId,
            }));
        }
    }
    updateCustomerInfo() {
        if (this.isMember) {
            store.dispatch(getCustomerInfo());
        }
        else {
            this.updateBasketWithNewCustomer();
        }
    }
    changeUserStatus() {
        if (this.isMember) {
            store.dispatch(setUserParams({ loggedIn: true }));
        }
    }
    get customerInfoRequestPayload() {
        const { givenName: firstName, phoneNumber, email: emailId, familyName: lastName, customerId, birth_year = null, birth_month = null, birth_day = null, postal_code = null, emailSubscription, phoneSubscription, } = this.authTokens?.accessToken?.claims ?? {};
        return {
            payload: {
                firstName,
                lastName,
                emailId,
                phoneNumber,
                attributes: {
                    birthDay: birth_day,
                    birthYear: birth_year,
                    birthMonth: birth_month,
                    postalCode: postal_code,
                },
                emailSubscription,
                phoneSubscription,
            },
            customerId,
        };
    }
    bootstrapAuthentication() {
        if (this.refreshAuthentication()) {
            this.initiateInterval();
        }
    }
    getAuthData(redirectUri, navUrl) {
        const { codeChallenge, codeVerifier } = SharedUtils.encryptionFlow();
        persistanceStorage?.setItem('codeChallenge', codeChallenge);
        persistanceStorage?.setItem('codeVerifier', codeVerifier);
        persistanceStorage?.setItem('redirectUri', redirectUri);
        if (navUrl) {
            persistanceStorage?.setItem('navUrl', navUrl);
        }
        return codeChallenge;
    }
    generateSignInUrl(redirectUri, isSignUpUrl = false, navUrl) {
        const codeChallenge = this.getAuthData(redirectUri, navUrl);
        this.clearIntervalIfExists();
        this.resetTokens();
        store.dispatch(clearTokens());
        const { memberScope, memberClientId, authResponseType, signInUrl, signUpUrl, appEnvironment, loginState, authCodeMethod, platform, } = this.authParams ?? {};
        const { tenantReducer = {} } = store?.getState() ?? {};
        const termsConditionsCheckbox = tenantReducer?.componentOptions?.accountSettings?.termsConditionsCheckbox;
        const marketingCommunicationCheckbox = tenantReducer?.componentOptions?.accountSettings?.marketingCommunicationCheckbox;
        const showVipContent = tenantReducer?.componentOptions?.web?.global?.showVipContent;
        const gtmKey = config(GTM_KEY);
        const showVipLogo = tenantReducer?.componentOptions?.web?.global?.showVipLogo;
        const showVipBanner = tenantReducer?.componentOptions?.web?.global?.showVipBanner;
        const showPostalCode = tenantReducer?.componentOptions?.web?.global?.showPostalCode;
        const showTermsConditions = tenantReducer?.componentOptions?.web?.global?.showTermsConditions;
        const showTermsUsePwa = tenantReducer?.componentOptions?.web?.global?.showTermsUsePwa;
        const showMandatoryItem = tenantReducer?.componentOptions?.web?.global?.showMandatoryItem;
        const showCreateAccountTitle = tenantReducer?.componentOptions?.web?.global?.showCreateAccountTitle;
        const showPersonalInfoAccountTitle = tenantReducer?.componentOptions?.web?.global?.showPersonalInfoAccountTitle;
        const showCreateAccountList = tenantReducer?.componentOptions?.web?.global?.showCreateAccountList;
        const uiLocales = App.envVars?.language?.split("-")[0];
        const market = tenantReducer?.countryCode;
        const domainUrl = isSignUpUrl ? signUpUrl : signInUrl;
        let signInParams = `${domainUrl}scope=${memberScope}&response_type=${authResponseType}` +
            `&client_id=${memberClientId}&redirect_uri=${redirectUri}` +
            `&state=${loginState}&code_challenge=${codeChallenge}` +
            `&code_challenge_method=${authCodeMethod}&platform=${platform}&env=${appEnvironment}&TermsConditions=${termsConditionsCheckbox}&MarketPreference=${marketingCommunicationCheckbox}`;
        signInParams = showVipContent ? `${signInParams}&showVipContent=${showVipContent}` : signInParams;
        signInParams = showVipLogo ? `${signInParams}&showVipLogo=${showVipLogo}` : signInParams;
        signInParams = showVipBanner ? `${signInParams}&showVipBanner=${showVipBanner}` : signInParams;
        signInParams = showPostalCode ? `${signInParams}&showPostalCode=${showPostalCode}` : signInParams;
        signInParams = showTermsConditions ? `${signInParams}&showTermsConditions=${showTermsConditions}` : signInParams;
        signInParams = showTermsUsePwa ? `${signInParams}&showTermsUsePwa=${showTermsUsePwa}` : signInParams;
        signInParams = showMandatoryItem ? `${signInParams}&showMandatoryItem=${showMandatoryItem}` : signInParams;
        signInParams = showPersonalInfoAccountTitle ? `${signInParams}&showPersonalInfoAccountTitle=${showPersonalInfoAccountTitle}` : signInParams;
        signInParams = showCreateAccountTitle ? `${signInParams}&showCreateAccountTitle=${showCreateAccountTitle}` : signInParams;
        signInParams = showCreateAccountList ? `${signInParams}&showCreateAccountList=${showCreateAccountList}` : signInParams;
        signInParams = market ? `${signInParams}&market=${market}` : signInParams;
        signInParams = gtmKey ? `${signInParams}&gtmKey=${gtmKey}` : signInParams;
        signInParams = App.envVars?.language ? `${signInParams}&lan=${App.envVars?.language}&ui_locales=${uiLocales}` : signInParams;
        signInParams = this.sendAppBaseUrl(platform, tenantReducer?.allowXLanguage, signInParams);
        return signInParams;
    }
}
// const instance = AuthManager.getInstance();
export default AuthManager;
